<template>
    <div class="offspring-edit-view flex w-full gap-5 h-full justify-content-start flex-column">
        <h2 class="offspring-edit-header">{{ $t('message.horseEdit.offspring.header') }} ({{ horse.name }})</h2>
        <DataTable :value="offsprings" lazy paginator dataKey="id"
                   v-model:rows="searchCriteria.page.limit" v-model:first="searchCriteria.page.offset" removableSort
                   :totalRecords="totalRecords" :loading="loading" @page="onPage"
                   @sort="onPage" :rowsPerPageOptions="[5,10,20,50]"
                   filterDisplay="row" v-model:filters="searchCriteria" scrollable
                   scrollHeight="calc(100vh - 300px)">
            <Column field="ueln" :header="$t('message.horseEdit.offspring.table.ueln')" sortable class="w-2"
                    :showFilterMenu="false" :showClearButton="false"
                    filterField="ueln">
                <template #body="{ data }">
                    {{ data.ueln }}
                </template>
                <template #filter>
                    <CustomInputText name="ueln" v-model="searchCriteria.ueln.value" @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="name" :header="$t('message.horseEdit.offspring.table.name')" sortable class="w-2"
                    :showFilterMenu="false" :showClearButton="false"
                    filterField="name">
                <template #body="{ data }">
                    {{ data.name }}
                </template>
                <template #filter>
                    <CustomInputText name="name" v-model="searchCriteria.name.value" @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="horseSexName" :header="$t('message.horseEdit.offspring.table.gender')" sortable class="w-1"
                    :showFilterMenu="false" :showClearButton="false"
                    filterField="sex" sortField="sex">
                <template #body="{ data }">
                    {{ data.horseSexName }}
                </template>
                <template #filter>
                    <CustomMultiSelectHorseSex name="sex" v-model="searchCriteria.sex.value" @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="coatColor" :header="$t('message.horseEdit.offspring.table.coatColor')" sortable class="w-2"
                    :showFilterMenu="false" :showClearButton="false"
                    filterField="coatColor" sortField="color.shortName">
                <template #body="{ data }">
                    {{ data.coatColor }}
                </template>
                <template #filter>
                    <CustomMultiSelectHorseColor name="coatColorIds" v-model="searchCriteria.coatColorIds.value" @update:modelValue="search" item-label="shortName"/>
                </template>
            </Column>
            <Column field="dateOfBirth" :header="$t('message.horseEdit.offspring.table.dateOfBirth')" sortable class="w-2"
                    :showFilterMenu="false" :showClearButton="false"
                    filterField="dateOfBirth">
                <template #body="{ data }">
                    {{ data.dateOfBirth }}
                </template>
                <template #filter>
                    <CustomCalendar name="dateOfBirth" v-model="searchCriteria.dateOfBirth.value" selectionMode="range"
                                    :showTime="false" placeholder="DD-MM-YYYY" @update:modelValue="search"
                                    showButtonBar/>
                </template>
            </Column>
            <Column field="previousStudbookRegistryId" :header="$t('message.horseEdit.offspring.table.originalStudbookRef')" sortable class="w-2"
                    :showFilterMenu="false" :showClearButton="false"
                    filterField="previousStudbookRegistryId">
                <template #body="{ data }">
                    {{ data.previousStudbookRegistryId }}
                </template>
                <template #filter>
                    <CustomInputText name="previousStudbookRegistryId" v-model="searchCriteria.previousStudbookRegistryId.value" @update:modelValue="search"/>
                </template>
            </Column>
            <Column :header="$t('message.horseEdit.offspring.table.details')" sortable class="w-1">
                <template #body="{ data }">
                    <router-link :to="`/horse/edit/${data.id}`">
                        <i class="pi pi-arrow-right" />
                    </router-link>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {FilterMatchMode} from "primevue/api";
import CustomInputText from "@/components/form/CustomInputText.vue";
import {
    searchHorseOffspring,
    searchHorseOffspringCount,
} from "@/swagger/vue-api-client";
import LibyanOrnament from "@/components/LibyanOrnament.vue";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import CustomMultiSelectHorseColor from "@/components/form/CustomMultiSelectHorseColor.vue";
import CustomMultiSelectHorseSex from "@/components/form/CustomMultiSelectHorseSex.vue";
import {SearchCriteria} from "@/utils/SearchCriteria";

export default {
    name: "HorseEditOffspringView",

    components: {
        Column,
        DataTable,
        LibyanOrnament,
        CustomInputText,
        CustomMultiSelectHorseSex,
        CustomMultiSelectHorseColor,
        CustomCalendar,
    },

    props: {
        horse: null,
    },

    data() {
        return {
            offsprings: [],
            loading: false,
            totalRecords: 0,
            searchCriteria: this.getClearSearchCriteria(),
        };
    },

    mounted() {
        this.onPage(this.getFirstPage());
    },

    methods: {
        search() {
            this.onPage(this.getFirstPage());
        },

        onPage(event) {
            this.loading = true;

            this.searchCriteria.page.offset = event.first;
            this.searchCriteria.page.limit = event.rows;
            this.searchCriteria.page.sortField = event.sortField;
            this.searchCriteria.page.sortOrder = event.sortOrder;

            const body = SearchCriteria.prepareCriteria(this.searchCriteria);

            body.parentId = this.$route.params.id || this.horse.id;

            searchHorseOffspring({body}).then((response) => {
                this.offsprings = response.data;
                return searchHorseOffspringCount({body})
                    .then((response) => {
                        this.totalRecords = response.data;
                        this.loading = false;
                    });
            });
        },

        getFirstPage() {
            return {
                first: this.searchCriteria.page.offset,
                rows: this.searchCriteria.page.limit,
            };
        },

        getClearSearchCriteria() {
            return {
                ueln: { value: null, matchMode: FilterMatchMode.CONTAINS },
                name: { value: null, matchMode: FilterMatchMode.CONTAINS },
                sex: { value: null, matchMode: FilterMatchMode.IN },
                coatColorIds: { value: null, matchMode: FilterMatchMode.IN },
                dateOfBirth: { value: null, matchMode: FilterMatchMode.IN },
                previousStudbookRegistryId: { value: null, matchMode: FilterMatchMode.CONTAINS },
                page: {
                    limit: 10,
                    offset: 0,
                    sortField: null,
                    sortOrder: null,
                },
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.offspring-edit-header {
    color: var(--accent-color);
    font-weight: 500;
    font-size: 35px;
    font-family: var(--decor-font-family);
}
</style>
